import { api } from './_client';
import type {
  FlightShare,
  FlightShareCreateRequest,
  FlightShareGrantResponse,
  FlightShareListResponse,
  ShareCounterparties,
  ShareDirection,
  ShareableDronesResponse,
} from '../../types/api';

/**
 * Flight sharing (#1182 Stage 3 UI over the Stage 2 endpoints).
 *
 * A separate domain module rather than a few more methods on `files.ts`
 * (STD-004): these routes are `/api/flight-shares`, they resolve
 * `fleet.flights` rows and never touch an S3 object, and the backend split
 * them out of `files.py` for the same reason.
 */
export const flightSharesApi = {
  /**
   * Shares the caller's org granted or received. All filters are optional.
   *
   * `direction`, `counterpartyOrganizationId`, `limit` and `offset` are
   * server-side on purpose. Filtering or slicing a page the client already
   * holds would answer "what does Talentis have?" with "…of the 50 rows I
   * happen to have fetched", which is not the question.
   */
  async listFlightShares(params?: {
    flightId?: string;
    droneSerial?: string;
    includeRevoked?: boolean;
    direction?: ShareDirection;
    counterpartyOrganizationId?: string;
    limit?: number;
    offset?: number;
  }): Promise<FlightShareListResponse> {
    const query = new URLSearchParams();
    if (params?.flightId) query.append('flight_id', params.flightId);
    if (params?.droneSerial) query.append('drone_serial', params.droneSerial);
    // Only sent when true — the backend default is false, and an explicit
    // `include_revoked=false` would make the query key noisier for no gain.
    if (params?.includeRevoked) query.append('include_revoked', 'true');
    if (params?.direction) query.append('direction', params.direction);
    if (params?.counterpartyOrganizationId) {
      query.append('counterparty_organization_id', params.counterpartyOrganizationId);
    }
    // Sent whenever they are numbers, `offset=0` included: unlike the flags
    // above, a paging window is worth stating explicitly in the URL, so a
    // request in a network log or a Sentry breadcrumb says which page failed.
    if (typeof params?.limit === 'number') query.append('limit', String(params.limit));
    if (typeof params?.offset === 'number') query.append('offset', String(params.offset));

    const qs = query.toString();
    const response = await api.get<FlightShareListResponse>(
      `/api/flight-shares${qs ? `?${qs}` : ''}`
    );
    return response.data;
  },

  /**
   * Grant read/write on flights to another organization.
   *
   * Two forms, and the caller must pick exactly one (the backend 422s
   * otherwise): explicit `flight_ids` for the right-click case, or
   * `drone_serial` for "share every flight for drone 102 with Mvented" as a
   * single call that writes N rows.
   */
  async grantFlightShares(body: FlightShareCreateRequest): Promise<FlightShareGrantResponse> {
    const response = await api.post<FlightShareGrantResponse>('/api/flight-shares', body);
    return response.data;
  },

  /**
   * Revoke a share by stamping `revoked_at`.
   *
   * A POST, not a DELETE: the row is never deleted (it is the audit trail of
   * who could see what, when), and only the granting org may do it.
   */
  async revokeFlightShare(shareId: string): Promise<FlightShare> {
    const response = await api.post<FlightShare>(`/api/flight-shares/${shareId}/revoke`);
    return response.data;
  },

  /**
   * Organizations this caller may legally share with, plus its own org id.
   *
   * Hub-and-spoke as data rather than as a rule the UI re-implements: XER gets
   * every customer org, a customer gets only XER. Not `/auth/organizations` —
   * that is XER-admin-only (so a customer admin could never populate a picker)
   * and includes orgs that are not legal grantees.
   */
  async getShareCounterparties(): Promise<ShareCounterparties> {
    const response = await api.get<ShareCounterparties>('/api/flight-shares/organizations');
    return response.data;
  },

  /**
   * The drones this caller's org can share flights of (#1498).
   *
   * Not `getAllDrones`: that lists `fleet.drones`, whose SELECT policy has an
   * XER-wide arm, so an XER admin was offered every airframe on the platform —
   * including customer-owned drones with no XER flights, which the grant could
   * only ever answer `SHARE_NO_FLIGHTS` for. This is derived from the caller's
   * own `fleet.flights` rows, the exact set the `drone_serial` grant resolves.
   */
  async getShareableDrones(): Promise<ShareableDronesResponse> {
    const response = await api.get<ShareableDronesResponse>('/api/flight-shares/drones');
    return response.data;
  },
};
